import React, { useCallback, useEffect, useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import { MessageDialogPopup } from '@sales-center/components'
import { ErrorModalState } from '@user/features/MapView/EntityDetailsDrawer/DrawerContentsSingle/hooks'
import { ENTITY_TYPES } from '@sales-center/common'
import { useMapViewFeatureEnabled } from '@user/store/tenantFeatures/hooks/useMapViewFeatureEnabled'
import {
  createLocationPermission,
  LocationCreatePermOps,
  useCheckPermissions,
} from '@user/store/authorization/permissions'
import { PestPacLocationActions } from './PestPacLocationActions'

type Props = {
  entityId: number
  entityType: ENTITY_TYPES
  errorModal: ErrorModalState
  setErrorModal: (errorModal: ErrorModalState) => void
  onOpenSvc: () => void
  refetchEntityDetails: () => void
}

export const DrawerContentErrorModal = (props: Props) => {
  const {
    errorModal,
    setErrorModal,
    onOpenSvc,
    entityId,
    entityType,
    refetchEntityDetails,
  } = props
  const mapViewFeatureEnabled = useMapViewFeatureEnabled()

  const [canCreateLocations] = useCheckPermissions(
    createLocationPermission as LocationCreatePermOps,
  )

  const showPestPacActions =
    mapViewFeatureEnabled &&
    !!errorModal.showPestPacActions &&
    canCreateLocations

  const [dismissLocked, setDismissLocked] = useState(false)

  const mountedRef = useRef(true)
  useEffect(
    () => () => {
      mountedRef.current = false
    },
    [],
  )

  const onSaveSuccess = useCallback(() => {
    if (!mountedRef.current) return
    setErrorModal({
      ...errorModal,
      open: false,
      message: '',
      onRetry: undefined,
    })
    if (errorModal.onRetry) {
      errorModal.onRetry()
    } else {
      refetchEntityDetails()
    }
  }, [errorModal, refetchEntityDetails, setErrorModal])

  return (
    <MessageDialogPopup
      cancelClick={() => {
        if (dismissLocked) return
        setErrorModal({ ...errorModal, open: false })
      }}
      paddingRight={4.5}
      message={<Alert severity="error">{errorModal.message}</Alert>}
      cancelButtonText={'Close'}
      open={errorModal.open}
      title={errorModal.title}
      cancelButtonVariant="text"
      {...(errorModal.showAddServiceButton && {
        actionButtonsPlacement: 'start',
        actionButtons: [
          <Button
            color="primary"
            data-test-id="map-view-add-service-product-button"
            key="map-view-add-service-product-button"
            onClick={() => {
              setErrorModal({ ...errorModal, open: false })
              onOpenSvc()
            }}
            variant="outlined"
          >
            Add Service or Product
          </Button>,
        ],
      })}
    >
      {showPestPacActions && (
        <PestPacLocationActions
          entityId={entityId}
          entityType={entityType}
          onSaveSuccess={onSaveSuccess}
          onSavingChange={setDismissLocked}
        />
      )}
    </MessageDialogPopup>
  )
}
DrawerContentErrorModal.displayName = 'DrawerContentErrorModal'
